import { dateOptionsShort } from 'constants/dateFormats';
import useAuth from 'hooks/useAuth';
import useErrorSnackbar from 'hooks/useErrorSnackbar';
import useSendText from 'hooks/useSendText';
import useSuccessSnackbar from 'hooks/useSuccessSnackbar';
import { Event } from 'types/event';
import { storedGigEventDate } from 'utils/gigEventLink';

export default function useStatusChangeActions() {
  const { db, user } = useAuth();
  const errorSnackbar = useErrorSnackbar();
  const successSnackbar = useSuccessSnackbar();
  const sendText = useSendText();

  const onMarkedDead = async (eventId: string, originalObject: Event) => {
    const { eventTitle, dateStart } = originalObject || {};
    const date = dateStart?.toLocaleDateString(['en-US'], dateOptionsShort);
    const textToSend = `On The Move: Thank you for holding ${eventTitle} on ${date}. Unfortunately, this event has not been booked. Please release the hold on your calendar for this date.`;
    try {
      const historyPromise = db.collection('histories').add({
        created: new Date(),
        modified: new Date(),
        createdBy: user?.name || '',
        history: 'Marked dead',
        category: 'event',
        relEventId: eventId
      });

      const gigsQuery = await db.collection('gigs').where('relEventId', '==', eventId).get();
      const relVendorIds = gigsQuery.docs
        .map((doc) => doc.data().relVendorId)
        .filter((id) => id !== undefined);

      const textPromises = relVendorIds.map((id) => sendText(id, textToSend));
      const updatePromises = gigsQuery.docs.map((doc) =>
        doc.ref.update({ contractStatus: 'Cancelled' })
      );

      await Promise.all([historyPromise, ...textPromises, ...updatePromises]);

      successSnackbar('Event marked dead, history created and texts sent');
    } catch (error) {
      console.error('Error marking event dead:', error);
    }
  };

  // `newDate` comes from useSaveObject's diff, already in stored form.
  const onDateChange = async (eventId: string, newDate: Date | null) => {
    try {
      const query = await db.collection('gigs').where('relEventId', '==', eventId).get();
      if (query.empty) {
        return;
      }

      const normalizedDate = storedGigEventDate(newDate);

      await Promise.all(
        query.docs.map((doc) => doc.ref.update({ relEventDate: normalizedDate }))
      );
      successSnackbar('Date changed for all gigs');
    } catch (error) {
      errorSnackbar(error as Error);
    }
  };

  return { onMarkedDead, onDateChange };
}
